import * as React from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { trpc, trpcUtils } from "@editor/utils/trpc";

import { useNavigate, useSearchParams } from "react-router-dom";

/**
 * Hook specifically for handling the Shopify integration connection
 * *during* the onboarding flow. It checks for a pendingAppInstallationId or shopifyIntegrationId
 * search parameter and attempts to connect it to the user's newly created project.
 */
export default function useConnectShopifyIntegrationOnboarding({
  targetPath,
}: {
  targetPath: string;
}) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const logEvent = useLogAnalytics();
  const [searchParams] = useSearchParams();
  const pendingAppInstallationId = searchParams.get("pendingAppInstallationId");
  const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");

  const handleSuccess = () => {
    void trpcUtils.project.listWithStats.invalidate();
    void trpcUtils.workspace.getById.invalidate();

    if (user) {
      logEvent("onboarding.flow.step.completed", {
        userId: user.email,
        stepId: "setup",
        stepType: "setup",
        stepData: {
          shopifyIntegrationId,
          pendingAppInstallationId,
        },
      });
    }

    navigate(targetPath!);
  };
  const {
    mutate: connectProjectToPendingInstallation,
    isPending: isConnectingProjectToPendingInstallation,
    isError: isProjectPendingConnectionError,
  } = trpc.project.connectToPendingInstallation.useMutation({
    onSuccess: () => handleSuccess(),
  });
  const {
    mutate: connectProjectToExistingIntegration,
    isPending: isConnectingProjectToIntegration,
    isError: isProjectIntegrationConnectionError,
  } = trpc.project.connectToIntegration.useMutation({
    onSuccess: () => handleSuccess(),
  });

  const { data: userWorkspaces, isFetching: isUserWorkspacesDataLoading } =
    trpc.workspace.getUserWorkspacesList.useQuery();
  const { data: userProjects, isFetching: isUserProjectsLoading } =
    trpc.project.listWithStats.useQuery({});

  const workspaceId = userWorkspaces?.workspaces[0]?.id;
  const projectId = userProjects?.projects[0]?.id;

  const isMutationLoading =
    isConnectingProjectToPendingInstallation ||
    isConnectingProjectToIntegration;
  const isMutationError =
    isProjectPendingConnectionError || isProjectIntegrationConnectionError;
  const isQueryLoading = isUserWorkspacesDataLoading || isUserProjectsLoading;

  React.useEffect(() => {
    // Note (Noah, 2025-02-25): If we have no pending app installation id AND no shopify integration id
    // in the params, then we didn't just come from Shopify. Just do nothing.
    if (!pendingAppInstallationId && !shopifyIntegrationId) {
      return;
    }

    // Note (Noah, 2025-02-25): If we're still loading then we might not have determined what to do in the
    // automatic post-onboarding case. Just return because we'll re-run this effect once we have the data
    if (isMutationLoading || isMutationError || isQueryLoading) {
      return;
    }

    if (
      !isUserWorkspacesDataLoading &&
      !isUserProjectsLoading &&
      !workspaceId &&
      !projectId
    ) {
      return;
    }

    // Note (Noah, 2025-02-25): Now we know that we came from Shopify, because we have a query param
    // indicating it.

    // NOTE (Matt 2024-09-26): If there is a pendingAppInstallationId, and a pendingIntegrationAutoConnectTarget
    // we should automatically connect the corresponding project or workspace to the pending install.
    if (pendingAppInstallationId) {
      if (projectId) {
        connectProjectToPendingInstallation({
          projectId,
          shopify: { pendingAppInstallationId },
        });
      } else {
        navigate(targetPath!);
      }
      return;
    } else if (shopifyIntegrationId) {
      if (projectId) {
        connectProjectToExistingIntegration({
          projectId,
          integrationId: shopifyIntegrationId,
        });
      } else {
        navigate(targetPath!);
      }
      searchParams.delete("shopifyIntegrationId");
    }
  }, [
    workspaceId,
    projectId,
    isUserWorkspacesDataLoading,
    isUserProjectsLoading,
    pendingAppInstallationId,
    shopifyIntegrationId,
    isMutationLoading,
    isMutationError,
    isQueryLoading,
    connectProjectToPendingInstallation,
    connectProjectToExistingIntegration,
    navigate,
    targetPath,
    searchParams,
  ]);

  return {
    isLoading: isMutationLoading || isQueryLoading,
    isError: isMutationError,
    isChecking: isUserWorkspacesDataLoading || isUserProjectsLoading,
    foundPendingInstall: Boolean(pendingAppInstallationId),
  };
}
